import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaCog,
  FaUserShield,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDown,
  FaAngleRight
} from 'react-icons/fa';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkspaceShell } from '../../../contexts/WorkspaceShellContext';
import {
  isNavParentActive,
  isNavPathActive,
  groupNavMenuItems
} from '../../../config/navMenuConfig';
import { isSuperAdminEmail } from '../../../config/superAdmin';
import BrandLogo from '../../common/BrandLogo';
import MercadoPagoMark from '../../common/MercadoPagoMark';
import UserAvatar from '../../common/UserAvatar';
import NavFlyout, { CLOSE_BRIDGE_MS } from './NavFlyout';
import useNavMenu from '../../../hooks/useNavMenu';

const RAIL_COLLAPSED = 64;
const RAIL_EXPANDED = 232;
const ICON_SIZE = 20;
const TAP_MIN = 40;
const MP_LICENSE_PATH = '/configuracion/empresa?licencia=1';

/**
 * Sidebar compacto/expandible — labels visibles sin depender del hover.
 */
const NavRail = memo(function NavRail({ interactionMode = 'hover' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, orgId } = useAuth();
  const { navExpanded, toggleNavExpanded } = useWorkspaceShell();
  const menuItems = useNavMenu();
  const railRef = useRef(null);
  const itemRefs = useRef({});
  const closeTimerRef = useRef(null);

  const [activeItem, setActiveItem] = useState(null);
  const [flyoutVisible, setFlyoutVisible] = useState(false);
  const [flyoutAnchorTop, setFlyoutAnchorTop] = useState(72);
  const [flyoutHeight, setFlyoutHeight] = useState(240);
  const [openGroups, setOpenGroups] = useState({});

  const railW = navExpanded ? RAIL_EXPANDED : RAIL_COLLAPSED;
  const sections = useMemo(() => groupNavMenuItems(menuItems), [menuItems]);

  const superAdmin = isSuperAdminEmail(currentUser?.email);
  const showMercadoPagoLink = Boolean(orgId);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setFlyoutVisible(false);
      setActiveItem(null);
    }, CLOSE_BRIDGE_MS);
  }, [clearCloseTimer]);

  const updateFlyoutAnchor = useCallback((item) => {
    const btn = itemRefs.current[item.label];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setFlyoutAnchorTop(rect.top);
    } else {
      setFlyoutAnchorTop(72);
    }
  }, []);

  const openFlyout = useCallback((item) => {
    if (!item.submenu?.length) return;
    clearCloseTimer();
    updateFlyoutAnchor(item);
    setActiveItem(item);
    setFlyoutVisible(true);
  }, [clearCloseTimer, updateFlyoutAnchor]);

  const closeFlyout = useCallback(() => {
    setFlyoutVisible(false);
    setActiveItem(null);
  }, []);

  const toggleInlineSubmenu = useCallback((label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  }, []);

  const handleItemClick = useCallback((item) => {
    if (item.submenu?.length) {
      if (navExpanded) {
        toggleInlineSubmenu(item.label);
        return;
      }
      if (interactionMode === 'click') {
        if (activeItem?.label === item.label && flyoutVisible) {
          closeFlyout();
        } else {
          openFlyout(item);
        }
      } else {
        openFlyout(item);
      }
      return;
    }
    if (item.path) {
      navigate(item.path);
      closeFlyout();
    }
  }, [
    navExpanded,
    interactionMode,
    activeItem,
    flyoutVisible,
    closeFlyout,
    openFlyout,
    navigate,
    toggleInlineSubmenu
  ]);

  const handleItemEnter = useCallback((item) => {
    if (navExpanded) return;
    if (interactionMode !== 'hover') return;
    if (item.submenu?.length) {
      openFlyout(item);
    } else {
      scheduleClose();
    }
  }, [navExpanded, interactionMode, openFlyout, scheduleClose]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (navExpanded) {
      closeFlyout();
    }
  }, [navExpanded, closeFlyout]);

  useEffect(() => {
    if (interactionMode === 'click' && flyoutVisible) {
      const onDocClick = (e) => {
        if (railRef.current?.contains(e.target)) return;
        if (e.target.closest?.('[data-nav-flyout]')) return;
        if (e.target.closest?.('[data-nav-flyout-bridge]')) return;
        closeFlyout();
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }
    return undefined;
  }, [interactionMode, flyoutVisible, closeFlyout]);

  useEffect(() => {
    if (!flyoutVisible || !activeItem) return undefined;
    const onScrollOrResize = () => updateFlyoutAnchor(activeItem);
    window.addEventListener('resize', onScrollOrResize);
    const navEl = railRef.current?.querySelector('nav');
    navEl?.addEventListener('scroll', onScrollOrResize);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      navEl?.removeEventListener('scroll', onScrollOrResize);
    };
  }, [flyoutVisible, activeItem, updateFlyoutAnchor]);

  // Auto-expand submenu of active parent when expanded
  useEffect(() => {
    if (!navExpanded) return;
    menuItems.forEach((item) => {
      if (item.submenu && isNavParentActive(location.pathname, item.submenu)) {
        setOpenGroups((prev) => (prev[item.label] ? prev : { ...prev, [item.label]: true }));
      }
    });
  }, [navExpanded, location.pathname, menuItems]);

  const renderItem = (item) => {
    const Icon = item.icon;
    const active = item.submenu
      ? isNavParentActive(location.pathname, item.submenu)
      : isNavPathActive(location.pathname, item.path);
    const isFlyoutTarget = activeItem?.label === item.label && flyoutVisible;
    const inlineOpen = Boolean(openGroups[item.label]);

    return (
      <div key={item.label} className="w-full px-1.5">
        <button
          ref={(el) => { itemRefs.current[item.label] = el; }}
          type="button"
          title={navExpanded ? undefined : item.label}
          aria-label={item.label}
          aria-expanded={item.submenu ? (navExpanded ? inlineOpen : isFlyoutTarget) : undefined}
          onClick={() => handleItemClick(item)}
          onMouseEnter={() => handleItemEnter(item)}
          className={`
            e0e3-rail-btn group relative flex w-full items-center gap-3 rounded-xl transition-[background,color,box-shadow] duration-150
            ${navExpanded ? 'px-2.5 py-2 justify-start' : 'justify-center'}
            ${active || isFlyoutTarget
              ? 'e0e3-rail-btn--active text-e0e3-accent bg-e0e3-accent/10'
              : item.highlight
                ? 'text-amber-200 hover:bg-amber-500/10 hover:text-amber-100'
                : 'text-e0e3-muted hover:bg-white/[0.06] hover:text-e0e3-foreground'}
          `}
          style={navExpanded ? { minHeight: TAP_MIN } : { width: TAP_MIN, height: TAP_MIN, minWidth: TAP_MIN, minHeight: TAP_MIN, margin: '0 auto' }}
        >
          {active && !navExpanded && (
            <span className="e0e3-rail-indicator absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-e0e3-accent shadow-[0_0_8px_rgba(30,144,255,0.55)]" />
          )}
          <Icon size={ICON_SIZE} className="shrink-0" aria-hidden />
          {navExpanded && (
            <>
              <span className="min-w-0 flex-1 truncate text-left text-sm font-medium">
                {item.label}
              </span>
              {item.highlight && !item.submenu?.length ? (
                <span className="shrink-0 rounded-full border border-amber-400/45 bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-100">
                  Nuevo
                </span>
              ) : null}
              {item.submenu?.length ? (
                inlineOpen ? <FaAngleDown size={12} className="opacity-70" /> : <FaAngleRight size={12} className="opacity-70" />
              ) : null}
            </>
          )}
        </button>

        {navExpanded && item.submenu?.length && inlineOpen && (
          <div className="mb-1 ml-3 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
            {item.submenu.map((sub) => {
              const subActive = isNavPathActive(location.pathname, sub.path);
              return (
                <button
                  key={sub.path}
                  type="button"
                  onClick={() => {
                    navigate(sub.path);
                    closeFlyout();
                  }}
                  className={`
                    flex w-full items-center rounded-lg px-2 py-1.5 text-left text-xs transition
                    ${subActive
                      ? 'bg-e0e3-accent/15 font-semibold text-e0e3-accent'
                      : 'text-e0e3-muted hover:bg-white/[0.05] hover:text-e0e3-foreground'}
                  `}
                >
                  {sub.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <aside
        ref={railRef}
        className="e0e3-workspace-rail fixed inset-y-0 left-0 z-[60] flex flex-col border-r border-white/[0.06] bg-e0e3-shell transition-[width] duration-200"
        style={{ width: railW }}
        aria-label="Navegación principal"
        data-expanded={navExpanded ? 'true' : 'false'}
        onMouseLeave={!navExpanded && interactionMode === 'hover' ? scheduleClose : undefined}
      >
        <div className={`flex h-[4.25rem] shrink-0 items-center border-b border-white/[0.06] ${navExpanded ? 'justify-between px-3' : 'justify-center px-1'}`}>
          <Link
            to="/"
            className="e0e3-rail-logo group relative flex h-12 items-center gap-2 rounded-xl transition hover:bg-white/[0.06]"
            title="0E3 Inicio"
          >
            <span className="e0e3-rail-logo-glow pointer-events-none absolute inset-1 rounded-xl" aria-hidden />
            <BrandLogo
              className="relative h-9 w-9 drop-shadow-[0_0_10px_rgba(30,144,255,0.5)] transition group-hover:scale-105"
              showText={false}
              variant="mark"
            />
            {navExpanded && (
              <span className="relative text-sm font-semibold tracking-wide text-e0e3-foreground">0E3 POS</span>
            )}
          </Link>
        </div>

        <nav className={`flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden py-2 scrollbar-thin ${navExpanded ? 'items-stretch' : 'items-center'}`}>
          {navExpanded
            ? sections.map((section) => (
              <div key={section.id} className="mb-2">
                <p className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-e0e3-muted/70">
                  {section.label}
                </p>
                {section.items.map(renderItem)}
              </div>
            ))
            : menuItems.map(renderItem)}
        </nav>

        <div className={`flex shrink-0 flex-col gap-1 border-t border-white/[0.06] py-2 ${navExpanded ? 'items-stretch px-2' : 'items-center'}`}>
          <button
            type="button"
            onClick={toggleNavExpanded}
            className={`flex items-center gap-2 rounded-xl text-e0e3-muted transition hover:bg-white/[0.06] hover:text-e0e3-foreground ${navExpanded ? 'px-3 py-2 text-xs font-medium' : 'justify-center'}`}
            style={!navExpanded ? { width: TAP_MIN, height: TAP_MIN } : undefined}
            title={navExpanded ? 'Compactar menú' : 'Expandir menú'}
            aria-label={navExpanded ? 'Compactar menú' : 'Expandir menú'}
          >
            {navExpanded ? <FaChevronLeft size={14} /> : <FaChevronRight size={14} />}
            {navExpanded && <span>Compactar</span>}
          </button>

          {showMercadoPagoLink ? (
            <Link
              to={MP_LICENSE_PATH}
              title="Licencia y Mercado Pago"
              className={`e0e3-rail-mp group flex items-center gap-2 rounded-lg transition duration-150 hover:bg-white/[0.06] ${navExpanded ? 'px-2 py-1.5' : 'flex-col justify-center px-1 py-1.5'}`}
              style={!navExpanded ? { width: TAP_MIN } : undefined}
            >
              <MercadoPagoMark className="h-5 w-[2.75rem] rounded opacity-90 transition group-hover:opacity-100" />
              {navExpanded && (
                <span className="text-[11px] font-semibold text-e0e3-muted group-hover:text-e0e3-foreground">Mercado Pago</span>
              )}
            </Link>
          ) : null}

          {superAdmin && (
            <Link
              to="/admin"
              title="Admin"
              className={`flex items-center gap-2 rounded-xl transition duration-150 ${location.pathname === '/admin' ? 'e0e3-rail-btn--active text-amber-300' : 'text-e0e3-muted hover:bg-white/[0.06] hover:text-e0e3-foreground'} ${navExpanded ? 'px-3 py-2' : 'justify-center'}`}
              style={!navExpanded ? { width: TAP_MIN, height: TAP_MIN } : undefined}
            >
              <FaUserShield size={18} />
              {navExpanded && <span className="text-sm">Admin</span>}
            </Link>
          )}
          <Link
            to="/configuracion/empresa"
            title="Configuración"
            className={`flex items-center gap-2 rounded-xl transition duration-150 ${location.pathname.startsWith('/configuracion') ? 'e0e3-rail-btn--active text-e0e3-accent' : 'text-e0e3-muted hover:bg-white/[0.06] hover:text-e0e3-foreground'} ${navExpanded ? 'px-3 py-2' : 'justify-center'}`}
            style={!navExpanded ? { width: TAP_MIN, height: TAP_MIN } : undefined}
          >
            <FaCog size={18} />
            {navExpanded && <span className="text-sm">Configuración</span>}
          </Link>
          <Link
            to="/perfil"
            title="Mi perfil"
            className={`mt-0.5 flex items-center gap-2 rounded-full transition hover:ring-2 hover:ring-e0e3-accent/40 ${navExpanded ? 'px-2 py-1' : 'justify-center'}`}
          >
            <UserAvatar user={currentUser} size="sm" />
            {navExpanded && (
              <span className="truncate text-xs text-e0e3-muted">{currentUser?.email || 'Perfil'}</span>
            )}
          </Link>
        </div>
      </aside>

      {!navExpanded && flyoutVisible && activeItem?.submenu && (
        <>
          <div
            data-nav-flyout-bridge
            className="fixed z-[64]"
            style={{
              left: railW,
              width: 12,
              top: Math.max(0, flyoutAnchorTop - 8),
              height: flyoutHeight + 16
            }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={interactionMode === 'hover' ? scheduleClose : undefined}
            aria-hidden
          />
          <NavFlyout
            item={activeItem}
            railWidth={railW}
            anchorTop={flyoutAnchorTop}
            onNavigate={closeFlyout}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={interactionMode === 'hover' ? scheduleClose : undefined}
            onHeightChange={setFlyoutHeight}
          />
        </>
      )}
    </>
  );
});

export default NavRail;
